// Bloco "Saúde do sistema" (#6508): só nas telas da Tecnologia. Quatro cartões — WhatsApp,
// robô da Equatorial, envio de e-mail e funções do servidor — com o estado de cada um e o
// que está acontecendo nas últimas 24 h.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Mail, MessageCircle, RefreshCw, Server, type LucideIcon } from 'lucide-react';
import { ROTULO_MOTIVO_ROBO } from '../Financeiro/lib/janelaFaturamentoLista';
import {
  piorEstado, saudeDasFuncoes, saudeDoEmail, saudeDoRobo, saudeDoWhatsapp, type EstadoSaude,
} from '../../src/lib/visaoGeral/saudeSistema';
import { plural } from './componentes';
import { useConexoesDoInbox, useEmailsDeCobranca, useEnviosComProblema, useFilaDoRoboAgora, useSaudeDasFuncoes } from './useSaudeDoSistema';

const ESTILO: Record<EstadoSaude, { rotulo: string; ponto: string; texto: string }> = {
  ok: { rotulo: 'Normal', ponto: 'bg-emerald-500', texto: 'text-emerald-700 dark:text-emerald-400' },
  atencao: { rotulo: 'Atenção', ponto: 'bg-amber-500', texto: 'text-amber-700 dark:text-amber-400' },
  fora: { rotulo: 'Fora do ar', ponto: 'bg-red-500', texto: 'text-red-700 dark:text-red-400' },
  sem_dado: { rotulo: 'Sem dado', ponto: 'bg-gray-300 dark:bg-gray-600', texto: 'text-gray-500 dark:text-gray-400' },
};

const dois = (n: number) => String(n).padStart(2, '0');
/** "21h40" se foi hoje, "28/10 13h20" se não. */
function quando(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const hoje = new Date();
  const hora = `${dois(d.getHours())}h${dois(d.getMinutes())}`;
  return d.toDateString() === hoje.toDateString() ? hora : `${dois(d.getDate())}/${dois(d.getMonth() + 1)} ${hora}`;
}
/** "há 8 h", "há 25 min", "há 3 dias". */
function haQuanto(iso: string | null | undefined): string {
  if (!iso) return '';
  const min = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  return h < 48 ? `há ${h} h` : `há ${Math.floor(h / 24)} dias`;
}

interface CartaoSaudeProps {
  titulo: string;
  icone: LucideIcon;
  /** null = carregando. */
  estado: EstadoSaude | null;
  resumo?: string;
  linhas?: (string | null | false | undefined)[];
  nota?: string;
  para?: { rotulo: string; caminho: string };
}

const CartaoSaude: React.FC<CartaoSaudeProps> = ({ titulo, icone: Icone, estado, resumo, linhas, nota, para }) => {
  const e = estado ? ESTILO[estado] : null;
  return (
    <div className="flex flex-col min-w-0 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white min-w-0">
          <Icone size={16} className="shrink-0 text-gray-400" /> <span className="truncate">{titulo}</span>
        </span>
        {e ? (
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap ${e.texto}`}>
            <span className={`h-2.5 w-2.5 rounded-full ${e.ponto}`} /> {e.rotulo}
          </span>
        ) : <span className="h-2.5 w-16 rounded-full bg-gray-100 dark:bg-gray-700 animate-pulse" />}
      </div>
      {estado === null ? (
        <div className="mt-3 h-16 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
      ) : (
        <>
          {resumo && <p className="mt-2 text-sm text-gray-800 dark:text-gray-100">{resumo}</p>}
          {linhas && linhas.some(Boolean) && (
            <ul className="mt-2 space-y-1 text-xs text-gray-500 dark:text-gray-400">
              {linhas.filter(Boolean).map((l, i) => <li key={i} className="break-words">{l}</li>)}
            </ul>
          )}
          {nota && <p className="mt-2 text-[11px] leading-snug text-gray-400">{nota}</p>}
        </>
      )}
      {para && (
        <Link to={para.caminho} className="mt-auto pt-3 text-xs font-semibold text-primary hover:underline">{para.rotulo}</Link>
      )}
    </div>
  );
};

export const BlocoSaudeDoSistema: React.FC = () => {
  const [versao, setVersao] = useState(0);
  const conexoes = useConexoesDoInbox(versao);
  const envios = useEnviosComProblema(versao);
  const robo = useFilaDoRoboAgora(versao);
  const emails = useEmailsDeCobranca(versao);
  const funcoes = useSaudeDasFuncoes(versao);

  // Hora da leitura: muda quando alguma busca termina.
  const agora = useMemo(() => new Date(), [conexoes.dados, envios.dados, robo.dados, emails.dados, funcoes.dados, funcoes.erro]);
  const w = conexoes.dados && envios.dados ? saudeDoWhatsapp(conexoes.dados, envios.dados, agora) : null;
  const r = robo.dados ? saudeDoRobo(robo.dados.fila, robo.dados.ultimaAtividade, agora) : null;
  const m = emails.dados && conexoes.dados ? saudeDoEmail(emails.dados, conexoes.dados, agora) : null;
  const carregandoFuncoes = !funcoes.dados && !funcoes.erro;
  const f = carregandoFuncoes ? null : saudeDasFuncoes(funcoes.dados, agora);
  const estados = [w?.estado, r?.estado, m?.estado, f?.estado];
  const geral = estados.every(Boolean) ? piorEstado(estados as EstadoSaude[]) : null;

  // ── textos ──
  const foraW = w?.conexoes.find(c => c.estado !== 'ok');
  const resumoW = !w ? undefined
    : w.estado === 'sem_dado' ? 'Nenhuma conexão de WhatsApp com atendente.'
    : foraW ? `${foraW.nome} ${foraW.estado === 'fora' ? 'fora do ar' : 'sem sinal'} desde ${quando(foraW.desde)}.`
    : w.conexoes.length === 1 ? `${w.conexoes[0].nome}: conectado.` : `${w.conexoes.length} conexões conectadas.`;

  const resumoR = !r ? undefined
    : r.parado ? `Parado: nenhuma tentativa desde ${quando(r.ultimaAtividade)}, com ${plural(r.pendentes, 'UC esperando', 'UCs esperando')}.`
    : r.presos ? `${plural(r.presos, 'UC travada', 'UCs travadas')} em "processando" desde ${quando(r.presosDesde)}.`
    : r.estado === 'atencao' ? `Sem tentativa ${haQuanto(r.ultimaAtividade)}, com ${plural(r.pendentes, 'UC esperando', 'UCs esperando')}.`
    : r.pendentes ? `Rodando: última tentativa ${haQuanto(r.ultimaAtividade)}.` : 'Nada esperando na fila.';

  const resumoM = !m ? undefined
    : m.caixas.some(c => !c.ativa) ? `Caixa de e-mail do Inbox fora: ${m.caixas.find(c => !c.ativa)!.nome}.`
    : m.falhas ? `${plural(m.falhas, 'e-mail de cobrança falhou', 'e-mails de cobrança falharam')} nas últimas 24 h.`
    : m.enviados ? `${plural(m.enviados, 'e-mail de cobrança enviado', 'e-mails de cobrança enviados')} nas últimas 24 h.`
    : `Nenhum e-mail de cobrança nas últimas 24 h${m.ultimoEnvio ? `; o último saiu em ${quando(m.ultimoEnvio)}` : ''}.`;

  const totalAgend = f?.agendamentos?.falhas.reduce((s, x) => s + x.falhas, 0) ?? 0;
  const totalFunc = f?.funcoes?.reduce((s, x) => s + x.erros, 0) ?? 0;
  const resumoF = !f ? undefined
    : funcoes.erro ? 'Não foi possível consultar. A função saude-sistema precisa estar publicada.'
    : totalAgend + totalFunc === 0 ? 'Nenhuma falha nas últimas 24 h.'
    : `${[
        totalAgend ? plural(totalAgend, 'agendamento falhou', 'agendamentos falharam') : null,
        totalFunc ? plural(totalFunc, 'chamada deu erro', 'chamadas deram erro') : null,
      ].filter(Boolean).join(' e ')} nas últimas 24 h; a última falha ${haQuanto(f.ultimaFalha)} (${quando(f.ultimaFalha)}).`;

  return (
    <section className="space-y-3" aria-label="Saúde do sistema">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Saúde do sistema</h2>
        {geral && <span className={`text-xs font-semibold ${ESTILO[geral].texto}`}>{ESTILO[geral].rotulo}</span>}
        <span className="text-xs text-gray-400 dark:text-gray-500">últimas 24 h · lido às {quando(agora.toISOString())}</span>
        <button type="button" onClick={() => setVersao(v => v + 1)}
          className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
          <RefreshCw size={12} /> Atualizar
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
        <CartaoSaude
          titulo="WhatsApp" icone={MessageCircle} estado={w?.estado ?? (conexoes.erro ? 'sem_dado' : null)}
          resumo={conexoes.erro ? 'Não foi possível ler as conexões.' : resumoW}
          linhas={w ? [
            ...w.conexoes.map(c => `${w.conexoes.length > 1 ? `${c.nome}: último` : 'Último'} sinal ${quando(c.ultimoSinal)}${c.motivo ? ` — ${c.motivo}` : ''}.`),
            w.falhasDeEnvio > 0 && `${plural(w.falhasDeEnvio, 'mensagem não saiu', 'mensagens não saíram')}${w.motivoDaFalha ? ` (${w.motivoDaFalha})` : ''}.`,
            w.enviosPresos > 0 && `${plural(w.enviosPresos, 'mensagem parada', 'mensagens paradas')} na fila de envio.`,
            w.falhasDeEnvio === 0 && w.enviosPresos === 0 && 'Nenhuma mensagem falhou ou ficou presa na fila.',
            w.naoMonitoradas > 0 && `${plural(w.naoMonitoradas, 'conexão de teste fica', 'conexões de teste ficam')} fora (sem atendente).`,
          ] : undefined}
          para={{ rotulo: 'Conexões do Inbox', caminho: '/inbox/configuracoes' }}
        />
        <CartaoSaude
          titulo="Robô da Equatorial" icone={Bot} estado={r?.estado ?? (robo.erro ? 'sem_dado' : null)}
          resumo={robo.erro ? 'Não foi possível ler a fila do robô.' : resumoR}
          linhas={r ? [
            `Na fila: ${plural(r.pendentes, 'UC', 'UCs')}${r.presos ? `; ${r.presos} em "processando" há mais de 1 h` : ''}.`,
            `Últimas 24 h: ${plural(r.falhas, 'falha', 'falhas')} e ${plural(r.concluidas, 'concluída', 'concluídas')}.`,
            r.porMotivo.length > 0 && r.porMotivo.slice(0, 3).map(([motivo, n]) => `${ROTULO_MOTIVO_ROBO[motivo]}: ${n}`).join(' · '),
            `Última tentativa: ${quando(r.ultimaAtividade)}.`,
          ] : undefined}
          nota="O robô roda num computador da empresa. Travado ou parado, alguém precisa reiniciar o programa lá."
          para={{ rotulo: 'Conferência do faturamento', caminho: '/financeiro/conferencia-faturamento' }}
        />
        <CartaoSaude
          titulo="Envio de e-mail" icone={Mail} estado={m?.estado ?? (emails.erro ? 'sem_dado' : null)}
          resumo={emails.erro ? 'Não foi possível ler os envios.' : resumoM}
          linhas={m ? [
            ...m.motivos.slice(0, 2).map(([motivo, n]) => `${n}× ${motivo}`),
            m.ultimoEnvio && `Último e-mail de cobrança enviado: ${quando(m.ultimoEnvio)}.`,
            ...m.caixas.map(c => `Caixa do Inbox ${c.nome}: ${c.ativa ? 'ativa' : `fora${c.motivo ? ` — ${c.motivo}` : ''}`}.`),
          ] : undefined}
          nota="Enviado = o Resend aceitou; a entrega ao cliente não é rastreada. Os outros envios (lembrete de assinatura, protocolo do rateio, cancelamento) não ficam registrados."
        />
        <CartaoSaude
          titulo="Funções do servidor" icone={Server} estado={f?.estado ?? null}
          resumo={resumoF}
          linhas={f && !funcoes.erro ? [
            f.agendamentos
              ? `Agendamentos do banco: ${plural(totalAgend, 'falha', 'falhas')} em ${f.agendamentos.execucoes.toLocaleString('pt-BR')} execuções.`
              : f.erroAgendamentos && `Agendamentos: não consultados (${f.erroAgendamentos}).`,
            ...f.agendamentosPorMotivo.slice(0, 2).map(g => `${g.falhas}× "${g.mensagem}" em ${plural(g.tarefas, 'tarefa', 'tarefas')}; a última às ${quando(g.ultima)}.`),
            f.funcoes && `Funções: ${plural(totalFunc, 'erro', 'erros')} de servidor (5xx).`,
            ...(f.funcoes?.slice(0, 3).map(x => `${x.funcao}: ${x.erros} de ${x.chamadas.toLocaleString('pt-BR')} chamadas${x.status ? ` (${x.status})` : ''} · ${quando(x.ultimaErro)}`) ?? []),
            !f.logsConfigurados && 'Erros das funções: falta o token de leitura dos logs (segredo SUPABASE_LOGS_TOKEN da função saude-sistema).',
            f.erroFuncoes && `Erros das funções: não consultados (${f.erroFuncoes}).`,
          ] : undefined}
        />
      </div>
    </section>
  );
};
